import { createContext } from 'react';
import { action, decorate } from 'mobx';
import platform from 'platform';
import { AuthAgent } from 'agents';
import query from 'query-string';
import { str_b64_encode_unicode } from 'helpers';
import { commonStore, catalogStore, leanplumStore } from 'stores';
import { URLS } from 'config';
import get from 'lodash/get';

class AuthStore {
    logout() {
        commonStore.setAuthenticatedUser(null);
        commonStore.setAuthorizationToken(null);
        commonStore.setSubscription(null);
        catalogStore.setCatalogs(null);

        return AuthAgent.logout();
    }

    current() {
        return AuthAgent.current();
    }

    logoutAllDevices() {
        commonStore.setAuthenticatedUser(null);
        commonStore.setAuthorizationToken(null);
        commonStore.setSubscription(null);
        catalogStore.setCatalogs(null);
        return AuthAgent.logoutAllDevices();
    }

    isLoggedIn() {
        return AuthAgent.logged();
    }

    async zendesk() {
        const params = query.parse(commonStore.routeLocation.search);

        if (params.zendeskRedirect && params.return_to && commonStore.user) {
            try {
                const res = await AuthAgent.zendeskToken();
                window.location.href = `${URLS.zendeskAccess}?jwt=${res.token}&return_to=${params.return_to}`;

                // avoid the website to load while we redirect the user
                return new Promise(() => {});
            } catch (e) {
                return true;
            }
        }
    }

    subscription() {
        return AuthAgent.subscription();
    }

    hasRequestedPrivacyDownload() {
        return AuthAgent.hasRequestedPrivacyDownload();
    }

    confirmEmail(token: string) {
        return AuthAgent.confirmEmail(token)
            .then(commonStore.retrieveCurrentUser)
            .then(commonStore.retrieveUserData);
    }

    tokenLogin(token: string) {
        let data: any = null;
        return AuthAgent.tokenLogin({
            token,
            loginPayload: this.getDeviceData(),
        })
            .then((res: any) => (data = res))
            .then(commonStore.retrieveCurrentUser)
            .then(commonStore.retrieveUserData)
            .then(() => data);
    }

    initAppleSignIn() {
        return AuthAgent.initAppleSignIn();
    }

    appleSignIn() {
        return AuthAgent.current()
            .then((user) => {
                commonStore.setLoadingUserData(true);
                leanplumStore.loginLeanplum(user.id);
                commonStore.setAuthenticatedUser(user);
            })
            .then(commonStore.retrieveUserData);
    }

    resetPassword(uid: string, token: string, password: string) {
        return AuthAgent.resetPassword(uid, token, password);
    }

    recoverPassword(email: string, url: string) {
        return AuthAgent.recoverPassword(email, url);
    }

    /**
     * Register a new ninja user.
     *
     * The only way to get this working is by generating a
     * unique ID for the user and storing it in local
     * storage or cookie. This solution hoever could
     * lead to users losing their profile in case
     * cookie/storage gets cleared.
     */
    registerNinja() {
        return AuthAgent.register({
            silent_auth_id: 'web-123457-fake-ninja-user',
        })
            .then(commonStore.retrieveCurrentUser)
            .then(commonStore.retrieveUserData);
    }

    /**
     *
     * @param {string} email
     * @param {string} password
     * @param {string} locale
     */
    register(email: string, password: string, locale: string) {
        const version = 2;
        const data = {
            ...this.getDeviceData(),
            email,
            password,
            version,
            locale,
        };

        return AuthAgent.register(data)
            .then((res) => {
                leanplumStore.setUserVariants(res);
                commonStore.setLoadingUserData(true);
            })
            .then(commonStore.retrieveCurrentUser)
            .then(commonStore.retrieveUserData);
    }

    /**
     * Returns some data useful for analytics
     * @return {object}
     */
    getDeviceData() {
        const params = query.parse(get(commonStore, 'routeLocation.search'));

        return {
            source: params.source || '',
            platform: platform && platform.os && platform.os.family,
            variants: leanplumStore.getVariantsString(),
            gdpr_introduced: true,
            app_type: 'ProfilePages',
        };
    }

    facebookLink(data: any) {
        return AuthAgent.facebookLink(data.accessToken).then(
            commonStore.retrieveCurrentUser,
        );
    }

    facebookUnlink() {
        return AuthAgent.facebookUnlink().then(commonStore.retrieveCurrentUser);
    }

    activateFacebookLoginToken(token: string, loginToken: string) {
        return AuthAgent.facebookLoginToken({
            token,
            loginToken,
            loginPayload: this.getDeviceData(),
        });
    }

    /**
     * Login or Signup with a Facebook token.
     *
     * @param {object} data
     */
    facebookAccess(data: any) {
        return AuthAgent.facebookAccess({
            token: data.accessToken,
            loginPayload: this.getDeviceData(),
        })
            .catch((error) => {
                console.log('YS', error);
                if (error && error.status === 404) {
                    // this means facebook login was successful but the user is not registered in yousician, we'll do it right away
                    return AuthAgent.register({
                        ...this.getDeviceData(),
                        email: data.email,
                        facebook_access_token: data.accessToken,
                    });
                    //TODO handle other cases
                    // 429 too many failed attempts
                }
            })
            .then(() => {
                commonStore.setLoadingUserData(true);
            })
            .then(commonStore.retrieveCurrentUser)
            .then(commonStore.retrieveUserData);
    }

    /**
     * Login with a Facebook token.
     *
     * @param {object} data
     */
    facebookLogin(data: any) {
        return AuthAgent.facebookAccess({
            token: data.accessToken,
            loginPayload: this.getDeviceData(),
        })
            .then(AuthAgent.current)
            .then((user) => {
                leanplumStore.loginLeanplum(user.id);
                commonStore.setLoadingUserData(true);
            })
            .then(commonStore.retrieveCurrentUser)
            .then(commonStore.retrieveUserData);
    }

    /**
     * Signup with a Facebook token.
     *
     * @param {object} data
     */
    facebookSignup(data: any) {
        const version = 2;
        return AuthAgent.register({
            ...this.getDeviceData(),
            email: data.email,
            facebook_access_token: data.accessToken,
            version,
        })
            .then((res) => {
                leanplumStore.setUserVariants(res);
                commonStore.setLoadingUserData(true);
            })
            .then(commonStore.retrieveCurrentUser)
            .then(commonStore.retrieveUserData);
    }

    /**
     * Send a request to to the server in order to login
     * the user based on the credentials provided.
     *
     * @param {string} username
     * @param {string} password
     * @param {bool} downloadData
     */
    login(username: string, password: string, downloadData = true) {
        const hash = str_b64_encode_unicode(`${username.trim()}:${password}`);

        if (!downloadData) {
            return AuthAgent.login({
                token: `Basic ${hash}`,
                loginPayload: { ...this.getDeviceData() },
            });
        }

        return AuthAgent.login({
            token: `Basic ${hash}`,
            loginPayload: { ...this.getDeviceData() },
        })
            .then(AuthAgent.current)
            .then((user) => {
                commonStore.setLoadingUserData(true);
                leanplumStore.loginLeanplum(user.id);
                commonStore.setAuthenticatedUser(user);
            })
            .then(commonStore.retrieveUserData);
    }
}

decorate(AuthStore, {
    logout: action,
    register: action,
    login: action,
    logoutAllDevices: action,
});

export const authStore = new AuthStore();
export default createContext(authStore);
